/**
 * LA LISTA ÚNICA DE «QUÉ PANTALLAS EXISTEN Y PARA QUIÉN» — I-251, brief 13 del diseño
 * (`handoff_claude_13_menu_lateral/README.md:5-7,112-120`).
 *
 * 🔴 POR QUÉ EXISTE (medido el 2026-10-01): la misma pregunta vivía escrita a mano en CUATRO
 *    sitios — `Sidebar.tsx` (NAV_SECTIONS), `CasaShell.tsx` (RIEL), `CommandPalette.tsx` (ITEMS) y
 *    `Topbar.tsx` (ROUTE_TITLES) — y no coincidían: la paleta llevaba `tareas` a `/tareas` y el
 *    menú a `/workspace`; le faltaban `tablero`, `contabilidad` y `overhead`; el riel seguía
 *    ofreciendo `/obra`. Quitar o poner una pantalla exigía cuatro cambios y alguien olvidaba uno.
 *
 * 🟢 AHORA: el menú, el riel, la paleta ⌘K y el título de la cabecera LEEN DE AQUÍ. Cambiar una
 *    entrada en este archivo cambia las cuatro superficies.
 *
 * ⛔ ESTO NO DECIDE PERMISOS. Los refleja: la regla vive en `accesoModulos.ts`
 *    (`tieneAccesoAModulo`, que manda el SERVIDOR vía `accesos` y `usuarios_modulos`) y aquí sólo
 *    se llama. Un menú que decidiera por su cuenta enseñaría lo que la ruta rebota.
 *
 * ⚠️ NO ES (y no se unifica con) `CommandPalette MODULO_A_RUTA` («¿a dónde lleva una notificación
 *    de ese módulo?») ni `useAIAssistant` («¿en qué módulo estoy?»): preguntan OTRA cosa.
 */
import type React from 'react';
import {
  Building2, LayoutDashboard, Landmark, BookOpen, FolderKanban, LayoutGrid,
  LayoutPanelLeft, BarChart3, Settings,
} from 'lucide-react';
import type { AccesoDeRol } from '@erp/shared';
import { tieneAccesoAModulo } from '@utils/accesoModulos';

export type GrupoDeMenu = 'Empresa' | 'Dinero' | 'Trabajo' | 'Sistema';

export interface EntradaDeNavegacion {
  id: string;
  label: string;
  descripcion: string;
  icono: React.ElementType;
  grupo: GrupoDeMenu;
  path: string;
  /** Módulo de `usuarios_modulos` que la abre. `null` = ninguno (sólo nivel). */
  modulo: string | null;
  /** Candado de nivel de la RUTA (README:150-152: sólo Tablero lo tiene). */
  minNivel?: number;
  /** Se dibuja con la marca del diseño (README:51-53) — la entrada se va a mudar al proyecto. */
  marca?: 'mudaAlProyecto';
  /** Lo que se escribe en ⌘K para llegar aquí. */
  keywords: string[];
  /** Icono del riel de dentro de un proyecto (README:96-99); sin él no sale en el riel. `orden` = el del diseño (inicio, proyectos, tesorería, gente, reportes, config). */
  riel?: { id: string; titulo: string; orden: number };
}

/** Ancho del menú lateral (README:32: columna de 214 px). UNA medida: el menú, el marco (`AppLayout`) y la cabecera (`Topbar`) la leen de aquí; con tres copias, al bajar de 240 a 214 el contenido se quedó 26 px separado. */
export const MENU_ANCHO = { abierto: 214, colapsado: 60 } as const;

export const GRUPOS_DE_MENU: readonly GrupoDeMenu[] = ['Empresa', 'Dinero', 'Trabajo', 'Sistema'];

/**
 * Las entradas. El orden manda: es el orden del menú, del riel y de la paleta.
 * Quedan 8 + Bancos marcado (decisión del ORQ: Bancos se queda hasta que existan las cuentas por
 * proyecto). Obra, Notificaciones (la abre la campana), Ingresos, Egresos y Post-venta NO están:
 * viven dentro de cada proyecto o en la campana.
 */
export const NAV_ENTRADAS: readonly EntradaDeNavegacion[] = [
  { id: 'proyectos', label: 'Proyectos', descripcion: 'Entrar a un proyecto', icono: FolderKanban, grupo: 'Empresa',
    path: '/proyectos', modulo: 'proyectos', keywords: ['proyectos', 'mapa', 'unidades', 'inventario', 'torres'],
    riel: { id: 'proyectos', titulo: 'Proyectos', orden: 2 } },
  { id: 'dashboard', label: 'Dashboard', descripcion: 'Resumen ejecutivo', icono: LayoutDashboard, grupo: 'Empresa',
    path: '/dashboard', modulo: 'dashboard', keywords: ['dashboard', 'inicio', 'resumen', 'home'],
    riel: { id: 'inicio', titulo: 'Inicio', orden: 1 } },
  { id: 'tablero', label: 'Tablero', descripcion: 'Compara proyectos entre sí', icono: LayoutGrid, grupo: 'Empresa',
    path: '/tablero', modulo: null, minNivel: 3, keywords: ['tablero', 'comparar', 'ejecutivo', 'semaforo'] },
  { id: 'bancos', label: 'Bancos', descripcion: 'Tesorería y conciliación', icono: Landmark, grupo: 'Dinero',
    path: '/bancos', modulo: 'bancos', marca: 'mudaAlProyecto',
    keywords: ['bancos', 'tesoreria', 'conciliacion', 'cuenta', 'saldo'],
    riel: { id: 'tesoreria', titulo: 'Tesorería', orden: 3 } },
  { id: 'contabilidad', label: 'Contabilidad', descripcion: 'Pólizas y cierre contable', icono: BookOpen, grupo: 'Dinero',
    path: '/contabilidad', modulo: 'contabilidad', keywords: ['contabilidad', 'polizas', 'iva', 'contpaqi'] },
  { id: 'overhead', label: 'Overhead', descripcion: 'Gasto que no es de ningún proyecto', icono: Building2, grupo: 'Dinero',
    path: '/overhead', modulo: 'overhead', keywords: ['overhead', 'oficina', 'corporativo', 'aportes'] },
  { id: 'tareas', label: 'Workspace', descripcion: 'Tareas y chat del equipo', icono: LayoutPanelLeft, grupo: 'Trabajo',
    path: '/workspace', modulo: 'tareas', keywords: ['workspace', 'tareas', 'chat', 'pendiente', 'asignar'],
    riel: { id: 'gente', titulo: 'Workspace', orden: 4 } },
  { id: 'reportes', label: 'Reportes', descripcion: 'Reportes financieros y ejecutivos', icono: BarChart3, grupo: 'Sistema',
    path: '/reportes', modulo: 'reportes', keywords: ['reportes', 'reporte', 'informe', 'ejecutivo', 'pdf'],
    riel: { id: 'reportes', titulo: 'Reportes', orden: 5 } },
  { id: 'configuracion', label: 'Configuración', descripcion: 'Ajustes del sistema', icono: Settings, grupo: 'Sistema',
    path: '/configuracion', modulo: 'configuracion', keywords: ['configuracion', 'ajustes', 'settings', 'perfil', 'usuarios'],
    riel: { id: 'config', titulo: 'Configuración', orden: 6 } },
];

/** Lo mínimo de la sesión que la regla necesita. */
/**
 * BUG-4092 (residual) — el nombre con el que la persona ve un módulo, para los avisos («Tu usuario no tiene acceso a Bancos»). Sale de la entrada del
 * menú (`NAV_ENTRADAS`: «tareas» se ve «Workspace»); un módulo que no está en el menú («egresos», «obra») sale con la primera en mayúscula. Nunca la clave cruda.
 */
export function nombreDelModulo(modulo: string): string {
  const clave = modulo.trim().toLowerCase();
  return NAV_ENTRADAS.find((e) => e.id === clave)?.label ?? (clave.charAt(0).toUpperCase() + clave.slice(1));
}

export interface SesionDeNavegacion {
  nivel: number;
  modulos: string[];
  accesos?: AccesoDeRol;
}

/**
 * ¿Esta persona ve esta entrada? Nivel primero (README:150), después LA regla de módulos de siempre.
 * Sin sesión no se dibuja nada: ni el menú ni la paleta ofrecen lo que no se puede abrir.
 */
export function veLaEntrada(e: EntradaDeNavegacion, s: SesionDeNavegacion): boolean {
  if (e.minNivel != null && s.nivel < e.minNivel) return false;
  if (e.modulo == null) return true;
  return tieneAccesoAModulo(s.modulos, e.modulo, s.nivel, s.accesos);
}

export const entradasVisibles = (s: SesionDeNavegacion): EntradaDeNavegacion[] =>
  NAV_ENTRADAS.filter((e) => veLaEntrada(e, s));

/** Cuántas entradas NO se dibujan para esta persona (la nota del pie del menú, README:55-57). */
export const cuantasSeOcultan = (s: SesionDeNavegacion): number =>
  NAV_ENTRADAS.length - entradasVisibles(s).length;

/** El riel de dentro de un proyecto: las visibles que traen icono de riel, en el orden de la lista. */
export const entradasDelRiel = (s: SesionDeNavegacion) =>
  entradasVisibles(s).filter((e) => e.riel).sort((a, b) => a.riel!.orden - b.riel!.orden);

/**
 * A dónde cae quien entra al sistema (Mau 1-oct: la primera pantalla es la lista de Proyectos). Si esa persona NO
 * ve «Proyectos» (módulo no asignado), cae en la primera entrada que sí ve — mandarla a una pantalla que la ruta
 * rebota le daría un aviso rojo en cada entrada. Sin ninguna visible, cae en el dashboard (no tiene candado de ruta).
 */
export function rutaDeAterrizaje(s: SesionDeNavegacion): string {
  const proyectos = NAV_ENTRADAS.find((e) => e.id === 'proyectos')!;
  if (veLaEntrada(proyectos, s)) return proyectos.path;
  return entradasVisibles(s)[0]?.path ?? '/dashboard';
}

/**
 * Pantallas de empresa que NO son una entrada del menú pero tienen título (la cabecera lo pide).
 * No son entradas: no salen en el menú, el riel ni la paleta.
 */
const TITULOS_APARTE: Record<string, string> = {
  '/notificaciones': 'Notificaciones',
  '/egresos': 'Egresos',
  '/chat': 'Tareas y Chat',
  '/tareas': 'Tareas y Chat',
};

/** Título de la cabecera para una ruta. Reemplaza al `ROUTE_TITLES` de `Topbar.tsx`. */
export function tituloDeRuta(pathname: string): string {
  const todas: Array<[string, string]> = [
    ...NAV_ENTRADAS.map((e): [string, string] => [e.path, e.label]),
    ...Object.entries(TITULOS_APARTE),
  ];
  const exacta = todas.find(([p]) => p === pathname);
  if (exacta) return exacta[1];
  const prefijo = todas.find(([p]) => p !== '/dashboard' && pathname.startsWith(p));
  return prefijo ? prefijo[1] : 'ERP RM';
}
